import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import authService from '../services/auth.service';
import {
  setGlobalTenantGetter,
  createApiHeaders,
  getApiBaseUrl,
  fetchCsrfToken,
  clearCsrfTokenCache,
} from '../config/api';
import { STORAGE_KEYS } from '../config/constants';
import { debugLog, debugWarn, debugError } from '../utils/debugLog';
import {
  extractAuthDataFromToken,
  extractTenantFromUrl,
  getTenantAlias,
  validateAndSyncTenant,
  createTenantObject,
  isAdmin as checkIsAdmin,
  isOwner as checkIsOwner,
  isAdminOrOwner as checkIsAdminOrOwner,
  hasGroupAccess as checkHasGroupAccess,
  getGroupIds as getGroupIdsHelper,
  getGroupNames as getGroupNamesHelper,
} from './AuthContextUtils';
import { useSessionVerification } from './AuthContextHooks';

// Create authentication context
const AuthContext = createContext();

// Custom hook to use authentication context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Authentication provider component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null); // Includes tenant info from AWS
  const [groups, setGroups] = useState([]); // User's groups from JWT token
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authInitialized, setAuthInitialized] = useState(false);

  // Get tenant alias string from tenant object or string
  const getTenantAliasCallback = useCallback(() => {
    return getTenantAlias(tenant);
  }, [tenant]);

  // Use session verification hook
  const { performBackgroundSessionVerification } = useSessionVerification({
    setUser,
    setTenant,
    setGroups,
    setIsAuthenticated,
  });

  // Debug: log current AuthContext user whenever it changes
  useEffect(() => {
    debugLog('AuthContext user updated', {
      hasUser: !!user,
      user,
    });
  }, [user]);

  // Initialize authentication on component mount
  useEffect(() => {
    initializeAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initialize authentication
  const initializeAuth = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch CSRF token on app initialization (for cross-origin requests)
      // This ensures we have the token before making any POST/PUT/DELETE requests
      try {
        await fetchCsrfToken();
      } catch (csrfError) {
        // Non-fatal: CSRF token fetch failure won't block app initialization
        // Backend will handle CSRF validation or allow API key bypass
        debugWarn('Failed to fetch CSRF token on initialization:', csrfError);
      }

      // Extract tenant from URL first (with security validation)
      const tenantFromUrl = extractTenantFromUrl();

      // Check if token exists (even if it might be slightly expired)
      const token = authService.getToken();
      const tokenIsValid = authService.isTokenValid();
      const storedUser = authService.getUser();
      const cachedTenant = authService.getTenant();

      // Debug: Log auth state from authService (single source of truth)
      debugLog('🔍 initializeAuth - Auth state from authService:', {
        hasToken: !!token,
        tokenPreview: token ? token.substring(0, 20) + '...' : null,
        tokenIsValid: tokenIsValid,
        hasStoredUser: !!storedUser,
        hasCachedTenant: !!cachedTenant,
        tenantAlias: cachedTenant
          ? typeof cachedTenant === 'string'
            ? cachedTenant
            : cachedTenant.alias || cachedTenant.tenant_id
          : null,
        tenantKeys: cachedTenant ? Object.keys(cachedTenant) : [],
      });

      // Validate and sync tenant from URL and localStorage
      const validatedTenant = validateAndSyncTenant(
        tenantFromUrl,
        cachedTenant
      );

      // If token exists, optimistically authenticate and let backend verify
      // This prevents redirect loops on refresh - backend makes final decision
      if (token) {
        // Optimistically mark authenticated immediately to prevent redirect loop
        setIsAuthenticated(true);

        // Extract groups from JWT token
        const authData = extractAuthDataFromToken(token);
        setGroups(authData.groups || []);

        // Seed user/tenant from cache if present
        if (storedUser) {
          // Merge groups and role from token if not in stored user
          const userWithGroups = {
            ...storedUser,
            groups: storedUser.groups || authData.groups || [],
            role: storedUser.role || authData.role || 'user',
          };
          setUser(userWithGroups);
        }

        if (validatedTenant) {
          // Create tenant object from validated tenant and cached tenant
          const tenantObj = createTenantObject(validatedTenant, cachedTenant);
          if (tenantObj) {
            setTenant(tenantObj);
          }
        }

        // Verify session in background (non-blocking with timeout)
        // This runs regardless of tokenIsValid or storedUser - backend decides
        performBackgroundSessionVerification(cachedTenant);
      } else {
        // No token → unauthenticated
        setIsAuthenticated(false);
        // Keep tenant info temporarily for redirect, will be cleared by ProtectedRoute
        // Use validated tenant (from URL or storage)
        if (validatedTenant) {
          const tenantObj =
            cachedTenant && typeof cachedTenant === 'object'
              ? cachedTenant
              : { alias: validatedTenant, tenant_id: validatedTenant };
          setTenant(tenantObj);
        } else {
          setTenant(null);
        }
        setUser(null);
        setGroups([]); // Clear groups when not authenticated
      }
    } catch (error) {
      debugError('❌ initializeAuth error:', error);
      setError(error.message);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
      setAuthInitialized(true);
      // Note: isAuthenticated, user, tenant are from closure - may be stale
      // The actual state values are set via setState calls above
      const currentToken = authService.getToken();
      const currentUser = authService.getUser();
      const currentTenant = authService.getTenant();
      debugLog('✅ initializeAuth finished:', {
        hasToken: !!currentToken,
        hasUser: !!currentUser,
        hasTenant: !!currentTenant,
        // Note: isAuthenticated state may not be updated yet (React state is async)
      });
    }
  };

  // Initialize Google Sign-In button when component mounts
  const initializeGoogleButton = useCallback(async () => {
    try {
      if (!window.google || !window.google.accounts) {
        return;
      }

      await authService.initializeGoogleAuth();

      // Render the Google Sign-In button
      const buttonContainer = document.getElementById('googleSignInButton');

      if (buttonContainer && !buttonContainer.hasChildNodes()) {
        window.google.accounts.id.renderButton(buttonContainer, {
          theme: 'outline',
          size: 'large',
          type: 'standard',
          text: 'signin_with',
          shape: 'rectangular',
        });
      }
    } catch (error) {
      // Handle initialization error silently
    }
  }, []);

  // Logout function - wrapped in useCallback for stability
  const logout = useCallback(async () => {
    try {
      setLoading(true);
      await authService.logout();

      setUser(null);
      setTenant(null);
      setIsAuthenticated(false);
      setError(null);
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Handle login success
  const handleLoginSuccess = (
    userData,
    tenantData,
    tenantSecretData = null
  ) => {
    // Extract groups and role from JWT token
    const token = authService.getToken();
    const authData = extractAuthDataFromToken(token);

    // Set groups from JWT token (or from userData if available)
    const groupsToSet = userData?.groups || authData.groups || [];
    setGroups(groupsToSet);

    // Merge user data with role from JWT token, preserving all original fields (including picture)
    const mergedUserData = {
      ...userData, // Preserve all original user data (email, name, picture, etc.)
      role: userData?.role || authData.role, // Add role if not present
      groups: groupsToSet, // Include groups in user object for consistency
    };

    // Ensure picture field is preserved (Google image URL)
    if (userData?.picture) {
      mergedUserData.picture = userData.picture;
    }

    setUser(mergedUserData);

    // Also save to localStorage to ensure persistence
    if (mergedUserData) {
      authService.setUser(mergedUserData);
    }

    debugLog('✅ handleLoginSuccess - User data merged:', {
      hasPicture: !!mergedUserData.picture,
      pictureUrl: mergedUserData.picture,
      hasEmail: !!mergedUserData.email,
      hasName: !!mergedUserData.name,
      role: mergedUserData.role,
      groupsCount: groupsToSet.length,
      groups: groupsToSet.map(g => g.group_name || g.group_id),
    });

    // Validate tenant consistency with URL
    const tenantFromUrl = extractTenantFromUrl();
    let finalTenant = tenantData;

    if (tenantFromUrl) {
      // URL tenant is authoritative
      const tenantDataAlias = tenantData
        ? typeof tenantData === 'string'
          ? tenantData
          : tenantData.alias || tenantData.tenant_id
        : null;

      if (tenantDataAlias && tenantDataAlias !== tenantFromUrl) {
        debugWarn(
          `Security: Backend tenant '${tenantDataAlias}' does not match URL tenant '${tenantFromUrl}'. Using URL tenant.`
        );
        finalTenant =
          typeof tenantData === 'object'
            ? { ...tenantData, alias: tenantFromUrl, tenant_id: tenantFromUrl }
            : { alias: tenantFromUrl, tenant_id: tenantFromUrl };
      } else if (!tenantDataAlias) {
        // Backend didn't return tenant, use URL tenant
        finalTenant = { alias: tenantFromUrl, tenant_id: tenantFromUrl };
      }
    }

    // Merge tenant secret (including adapters) into tenant object
    // This is the SINGLE SOURCE OF TRUTH for merging tenant_secret
    // We merge here (not in AuthCallback) because:
    // 1. We do tenant validation with URL which might create a new tenant object
    // 2. If a new tenant object is created, we need to merge tenant_secret
    // 3. This ensures adapters are always preserved even if tenant validation modifies the tenant
    debugLog('🔍 handleLoginSuccess - Before merge:', {
      hasTenantSecretData: !!tenantSecretData,
      hasFinalTenant: !!finalTenant,
      finalTenantKeys: finalTenant ? Object.keys(finalTenant) : [],
    });

    if (tenantSecretData && finalTenant) {
      // Merge secret data into tenant object
      finalTenant = {
        ...finalTenant,
        adapters: tenantSecretData.adapters || {}, // Include adapters from AWS secret
        tenant_secret: tenantSecretData, // Keep full secret for reference if needed
      };
      debugLog(
        '✅ Tenant secret merged into tenant object (including adapters):',
        {
          alias: finalTenant.alias || finalTenant.tenant_id,
          keys: Object.keys(finalTenant),
        }
      );
    } else if (tenantSecretData && !finalTenant) {
      // If we have secret but no tenant data, create tenant from secret
      finalTenant = {
        alias: tenantSecretData.tenant_id,
        tenant_id: tenantSecretData.tenant_id,
        adapters: tenantSecretData.adapters || {},
        tenant_secret: tenantSecretData,
      };
      debugLog(
        '✅ Created tenant object from secret data (including adapters):',
        {
          alias: finalTenant.alias || finalTenant.tenant_id,
          keys: Object.keys(finalTenant),
        }
      );
    } else if (!tenantSecretData) {
      debugWarn('⚠️ No tenant secret received in login response');
    }

    // Save merged tenant data to localStorage FIRST
    if (finalTenant) {
      debugLog('💾 Saving tenant to localStorage:', {
        alias: finalTenant.alias || finalTenant.tenant_id,
        keys: Object.keys(finalTenant),
      });
      authService.setTenant(finalTenant);

      // Verify it was saved
      const savedTenant = authService.getTenant();
      debugLog('✅ Verified tenant saved to localStorage:', {
        saved: !!savedTenant,
        keys: savedTenant ? Object.keys(savedTenant) : [],
      });
    } else {
      debugWarn('⚠️ No finalTenant to save to localStorage');
    }

    // Set React state AFTER saving to localStorage
    // This ensures localStorage is ready before components try to access it
    setTenant(finalTenant || null);

    // Extract groups from JWT token (if not already set earlier)
    const tokenForGroups = authService.getToken();
    if (tokenForGroups) {
      const authDataFromToken = extractAuthDataFromToken(tokenForGroups);
      if (authDataFromToken.groups && authDataFromToken.groups.length > 0) {
        setGroups(authDataFromToken.groups);
      }
    }

    setIsAuthenticated(true);
    setError(null);
    setLoading(false);
  };

  // Handle login error
  const handleLoginError = errorMessage => {
    setError(errorMessage);
    setLoading(false);
  };

  // Handle logout event
  const handleLogout = () => {
    setUser(null);
    setTenant(null); // Clears tenant on logout
    setGroups([]); // Clear groups on logout
    setIsAuthenticated(false);
    setError(null);
    // Clear CSRF token cache on logout
    clearCsrfTokenCache();
  };

  // Set up event listeners for authentication events
  useEffect(() => {
    const handleLoginSuccessEvent = event => {
      debugLog('✅ AuthContext received auth:login-success event:', {
        hasUser: !!event.detail.user,
        hasTenant: !!event.detail.tenant,
        hasTenantSecret: !!event.detail.tenant_secret,
        tenantSecretKeys: event.detail.tenant_secret
          ? Object.keys(event.detail.tenant_secret)
          : [],
      });
      handleLoginSuccess(
        event.detail.user,
        event.detail.tenant,
        event.detail.tenant_secret || null
      );
    };

    const handleLoginErrorEvent = event => {
      handleLoginError(event.detail.error);
    };

    const handleLogoutEvent = () => {
      handleLogout();
    };

    // Add event listeners
    window.addEventListener('auth:login-success', handleLoginSuccessEvent);
    window.addEventListener('auth:login-error', handleLoginErrorEvent);
    window.addEventListener('auth:logout', handleLogoutEvent);

    // Cleanup event listeners
    return () => {
      window.removeEventListener('auth:login-success', handleLoginSuccessEvent);
      window.removeEventListener('auth:login-error', handleLoginErrorEvent);
      window.removeEventListener('auth:logout', handleLogoutEvent);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Register global tenant getter for api.js (so api.js can use AuthContext)
  // Register immediately and update when tenant changes - this ensures it's always available
  useEffect(() => {
    setGlobalTenantGetter(getTenantAliasCallback);
    // Log for debugging
    if (tenant) {
      debugLog('✅ Global tenant getter registered:', {
        hasTenant: !!tenant,
      });
    }
  }, [getTenantAliasCallback, tenant]); // Re-register when tenant changes

  // Sync tenant from URL on navigation (watch for URL changes)
  useEffect(() => {
    const syncTenantFromUrl = () => {
      const tenantFromUrl = extractTenantFromUrl();
      if (tenantFromUrl) {
        const currentAlias = getTenantAliasCallback();
        if (currentAlias !== tenantFromUrl) {
          // URL tenant changed, update context
          const currentTenant = tenant; // Get from React state
          const cachedTenant = authService.getTenant(); // Get from localStorage

          const tenantObj = {
            alias: tenantFromUrl,
            tenant_id: tenantFromUrl,
          };

          // Preserve adapters from current tenant or cached tenant
          const sourceTenant = currentTenant || cachedTenant;
          if (
            sourceTenant &&
            typeof sourceTenant === 'object' &&
            sourceTenant.adapters
          ) {
            tenantObj.adapters = sourceTenant.adapters;
          }

          setTenant(tenantObj);
          // Update localStorage
          authService.setTenant(tenantObj);
        }
      }
    };

    // Sync on mount and URL changes
    syncTenantFromUrl();

    // Listen for popstate (browser back/forward)
    window.addEventListener('popstate', syncTenantFromUrl);

    return () => {
      window.removeEventListener('popstate', syncTenantFromUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Comprehensive method to refresh AuthContext with latest data from AWS Secrets Manager.
   *
   * This is the SINGLE SOURCE OF TRUTH method for updating AuthContext when any information
   * is added, deleted, or modified. Use this method whenever AuthContext needs to be refreshed.
   *
   * Flow:
   * 1. Backend invalidates AWS secret cache (via /api/v1/auth/refresh-tenant-secret endpoint)
   * 2. Backend fetches fresh data from AWS Secrets Manager (bypassing cache)
   * 3. Frontend removes old localStorage entry
   * 4. Frontend writes new data to localStorage
   * 5. Frontend updates React state
   *
   * @returns {Promise<Object|null>} Updated tenant object, or null if refresh failed
   *
   * @example
   * // After creating/deleting/updating AI provider:
   * const updatedTenant = await refreshAuthContext();
   * if (updatedTenant) {
   *   // Use updatedTenant.adapters for immediate UI update
   * }
   */
  const refreshAuthContext = useCallback(async () => {
    if (!tenant?.alias) {
      debugWarn('⚠️ Cannot refresh AuthContext: no tenant alias');
      return null;
    }

    const tenantAlias = tenant.alias;
    const previousAdapterCount = tenant.adapters
      ? Object.keys(tenant.adapters).length
      : 0;

    try {
      debugLog('🔄 Starting AuthContext refresh flow...', {
        tenantAlias,
        previousAdapterCount,
        step: 'STEP 1: Requesting backend to invalidate cache and fetch fresh data',
      });

      // STEP 1: Backend invalidates AWS secret cache and fetches fresh data
      // The /api/v1/auth/refresh-tenant-secret endpoint:
      // - Invalidates tenant secret cache (invalidate_tenant_secret_cache)
      // - Fetches fresh data from AWS Secrets Manager (use_cache=False)
      // - Returns fresh tenant secret data including adapters
      const headers = {
        ...createApiHeaders(),
        Authorization: `Bearer ${authService.getToken()}`,
      };
      const apiBaseUrl = getApiBaseUrl();
      const response = await fetch(
        `${apiBaseUrl}/api/v1/auth/refresh-tenant-secret`,
        {
          method: 'GET',
          headers,
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        debugError(
          `❌ Refresh endpoint returned ${response.status}: ${errorText}`
        );
        throw new Error(
          `Failed to refresh tenant secret: ${response.status} ${response.statusText}`
        );
      }

      const tenantSecretData = await response.json();

      if (!tenantSecretData) {
        debugWarn('⚠️ No tenant secret data received from refresh endpoint');
        return null;
      }

      if (!tenant) {
        debugWarn('⚠️ No tenant in state, cannot update');
        return null;
      }

      debugLog(
        '✅ STEP 1 complete: Fresh data received from AWS Secrets Manager',
        {
          adapterCount: tenantSecretData.adapters
            ? Object.keys(tenantSecretData.adapters).length
            : 0,
          adapterKeys: tenantSecretData.adapters
            ? Object.keys(tenantSecretData.adapters).sort()
            : [],
        }
      );

      // STEP 2: Create updated tenant object with fresh data
      // Create a NEW object to ensure React detects the change
      const updatedTenant = {
        ...tenant,
        adapters: tenantSecretData.adapters || {}, // Fresh adapters from AWS
        tenant_secret: tenantSecretData, // Keep full secret for reference
      };

      const newAdapterCount = Object.keys(updatedTenant.adapters || {}).length;
      const adapterKeys = Object.keys(updatedTenant.adapters || {}).sort();

      debugLog('✅ STEP 2 complete: Updated tenant object created', {
        newAdapterCount,
        previousAdapterCount,
        adapterKeys,
      });

      // STEP 3: Remove old localStorage entry and write new one
      // This ensures localStorage is completely refreshed, not just updated
      try {
        debugLog('🔄 STEP 3: Removing old localStorage entry...', {
          tenantKey: authService.tenantKey,
        });

        // Remove old entry
        authService.clearTenant();

        // Verify old entry was removed
        const clearedTenant = authService.getTenant();
        if (clearedTenant) {
          debugWarn(
            '⚠️ Old localStorage entry still exists after clearTenant(), forcing removal...'
          );
          localStorage.removeItem(authService.tenantKey);
        }

        debugLog('✅ Old localStorage entry removed');

        // Write new data to localStorage
        debugLog('🔄 Writing new data to localStorage...', {
          adapterCount: newAdapterCount,
          adapterKeys,
        });

        authService.setTenant(updatedTenant);

        // STEP 4: Verify localStorage was updated successfully
        const savedTenant = authService.getTenant();
        if (!savedTenant || !savedTenant.adapters) {
          debugError('❌ Failed to verify localStorage update after refresh');
          throw new Error('localStorage update verification failed');
        }

        const savedAdapterCount = Object.keys(
          savedTenant.adapters || {}
        ).length;
        if (savedAdapterCount !== newAdapterCount) {
          debugError('❌ localStorage adapter count mismatch after refresh', {
            expected: newAdapterCount,
            actual: savedAdapterCount,
          });
          throw new Error('localStorage adapter count mismatch');
        }

        // Verify adapter keys match
        const savedAdapterKeys = Object.keys(savedTenant.adapters || {}).sort();
        if (JSON.stringify(savedAdapterKeys) !== JSON.stringify(adapterKeys)) {
          debugError('❌ localStorage adapter keys mismatch after refresh', {
            expected: adapterKeys,
            actual: savedAdapterKeys,
          });
          throw new Error('localStorage adapter keys mismatch');
        }

        debugLog(
          '✅ STEP 3 complete: localStorage updated and verified successfully',
          {
            adapterCount: savedAdapterCount,
            adapterKeys: savedAdapterKeys,
          }
        );
      } catch (localStorageError) {
        debugError(
          '❌ STEP 3 failed: localStorage update error:',
          localStorageError
        );
        // Still update React state even if localStorage update fails
        // This ensures UI reflects the fresh data immediately
        // But log the error so we know localStorage is out of sync
      }

      // STEP 5: Update React state (triggers re-render of all consuming components)
      debugLog('🔄 STEP 4: Updating React state...');
      setTenant(updatedTenant);

      debugLog('✅ STEP 4 complete: React state updated');
      debugLog('✅ AuthContext refresh flow completed successfully', {
        tenantAlias,
        previousAdapterCount,
        newAdapterCount,
        adapterKeys,
      });

      // Return the updated tenant for immediate use (synchronous)
      return updatedTenant;
    } catch (error) {
      debugError('❌ AuthContext refresh flow failed:', error);
      // Don't throw - allow UI to continue with cached data
      return null;
    }
  }, [tenant]);

  /**
   * @deprecated Use refreshAuthContext() instead. This method is kept for backward compatibility.
   *
   * Helper function to refresh tenant adapters from backend (after create/update/delete).
   * Fetches fresh tenant secret from AWS Secrets Manager and updates AuthContext.
   * Returns the updated tenant object for synchronous use.
   */
  const refreshTenantAdapters = useCallback(async () => {
    debugWarn(
      '⚠️ refreshTenantAdapters() is deprecated. Use refreshAuthContext() instead.'
    );
    return refreshAuthContext();
  }, [refreshAuthContext]);

  // Helper functions for role and group access checks
  const isAdmin = useCallback(() => {
    return checkIsAdmin(user);
  }, [user]);

  const isOwner = useCallback(() => {
    return checkIsOwner(user);
  }, [user]);

  const isAdminOrOwner = useCallback(() => {
    return checkIsAdminOrOwner(user);
  }, [user]);

  const hasGroupAccess = useCallback(
    groupIds => {
      return checkHasGroupAccess(user, groups, groupIds);
    },
    [user, groups]
  );

  const getGroupIds = useCallback(() => {
    return getGroupIdsHelper(groups);
  }, [groups]);

  const getGroupNames = useCallback(() => {
    return getGroupNamesHelper(groups);
  }, [groups]);

  // Memoize context value to prevent unnecessary re-renders
  // Only re-create when dependencies change
  const value = useMemo(
    () => ({
      // State (current auth data)
      user,
      tenant, // Includes tenant info + adapters from AWS - available to all users
      groups, // User's groups from JWT token
      loading,
      error,
      isAuthenticated,
      authInitialized,

      // Update functions (functions that can update the state)
      logout,
      refreshAuthContext, // PRIMARY: Comprehensive method to refresh AuthContext (cache invalidation + localStorage + state)
      refreshTenantAdapters, // DEPRECATED: Use refreshAuthContext() instead. Kept for backward compatibility.

      // Helper functions
      authService,
      initializeGoogleButton,
      getTenantAlias: getTenantAliasCallback, // Export helper function
      storageKeys: STORAGE_KEYS, // Export storage keys from config (for consistency/validation)

      // Role and group access helpers
      isAdmin, // Check if user is admin
      isOwner, // Check if user is owner
      isAdminOrOwner, // Check if user is admin or owner
      hasGroupAccess, // Check if user has access to any of the specified groups
      getGroupIds, // Get array of user's group IDs
      getGroupNames, // Get array of user's group names
    }),
    [
      user,
      tenant,
      groups,
      loading,
      error,
      isAuthenticated,
      authInitialized,
      logout,
      refreshAuthContext,
      refreshTenantAdapters,
      initializeGoogleButton,
      getTenantAliasCallback,
      isAdmin,
      isOwner,
      isAdminOrOwner,
      hasGroupAccess,
      getGroupIds,
      getGroupNames,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
